import type { CategoryId } from './valueObjects/categoryId';
import type { CreateDatetime } from './valueObjects/createDatetime';
import type { LastModifiedDatetime } from './valueObjects/lastModifiedDatetime';
import type { MemberId } from './valueObjects/memberId';
import type { Title } from './valueObjects/title';
import type { NoticeItemJson } from '@/types/domain/notice/entities/noticeItemEntity';

import { Entity } from '@/domain/entity';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * 掲示板アイテムプロパティ
 */
export type NoticeItemProps = {
  /** 作成日時 */
  createDatetime: CreateDatetime,
  /** 作成者ID */
  createMemberId: MemberId,
  /** 最終更新日時 */
  lastModifiedDatetime: LastModifiedDatetime,
  /** 最終更新者 */
  lastModifiedMemberId: MemberId,
  /** カテゴリID */
  categoryId: CategoryId,
  /** タイトル */
  title: Title,
}

export class NoticeItemEntity extends Entity<
  NoticeItemProps,
  NoticeItemJson
> {
  /**
   * 削除タイプ
   */
  get deleteType(): DeleteType {
    return DeleteType.Dt;
  }

  /**
   * 削除キー
   */
  get deleteKey(): string {
    return this.getFutureYear(this.lastModifiedDatetime.value, 3).toJSON();
  }

  /**
   * データJsonを返す
   * @returns データ
   */
  toJsonData(): NoticeItemJson {
    return {
      createDatetime: this.createDatetime.value.toJSON(),
      createMemberId: this.createMemberId.value,
      lastModifiedDatetime: this.lastModifiedDatetime.value.toJSON(),
      lastModifiedMemberId: this.lastModifiedMemberId.value,
      categoryId: this.categoryId.value,
      title: this.title.value,
    };
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    return EntityName.WagakoAppNoticeNoticeItemEntity;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return `${this.categoryId.value}#${this.createDatetime.toStringDateTime()}`;
  }

  /**
   * サブキー
   */
  get subKey() {
    return `${this.lastModifiedDatetime.toStringDateTime()}#${this.lastModifiedMemberId.value}`;
  }

  /**
   * S3ファイル名
   */
  get s3Filename(): string {
    return 'd.json.gz';
  }

  /**
   * S3パス
   */
  get s3Path(): string {
    const name = this.entityName;
    const key = this.entityKey;
    const fname = this.s3Filename;
    return `${name}/${key}/${fname}`;
  }

  /**
   * 作成日時
   */
  get createDatetime(): CreateDatetime {
    return this._value.createDatetime;
  }

  /**
   * 作成日時
   */
  set createDatetime(createDatetime: CreateDatetime) {
    this._value.createDatetime = createDatetime;
  }

  /**
   * 作成メンバーID
   */
  get createMemberId(): MemberId {
    return this._value.createMemberId;
  }

  /**
   * 作成メンバーID
   */
  set createMemberId(createMemberId: MemberId) {
    this._value.createMemberId = createMemberId;
  }

  /**
   * 最終更新日時
   */
  get lastModifiedDatetime(): LastModifiedDatetime {
    return this._value.lastModifiedDatetime;
  }

  /**
   * 最終更新日時
   */
  set lastModifiedDatetime(lastModifiedDatetime: LastModifiedDatetime) {
    this._value.lastModifiedDatetime = lastModifiedDatetime;
  }

  /**
   * 最終更新メンバーID
   */
  get lastModifiedMemberId(): MemberId {
    return this._value.lastModifiedMemberId;
  }

  /**
   * 最終更新メンバーID
   */
  set lastModifiedMemberId(lastModifiedMemberId: MemberId) {
    this._value.lastModifiedMemberId = lastModifiedMemberId;
  }

  /**
   * カテゴリID
   */
  get categoryId(): CategoryId {
    return this._value.categoryId;
  }

  /**
   * カテゴリID
   */
  set categoryId(categoryId: CategoryId) {
    this._value.categoryId = categoryId;
  }

  /**
   * タイトル
   */
  get title(): Title {
    return this._value.title;
  }

  /**
   * タイトル
   */
  set title(title: Title) {
    this._value.title = title;
  }

  /**
   * コンテンツのS3パス
   */
  get contentS3Path(): string {
    return this.entityKey;
  }
}
